/**
 * Personal Mode Clone Table Limit Message
 *
 * Shows a message when Personal mode restricts cloning with data due to large table.
 * Explains the limit and provides an upgrade option. Schema-only cloning remains available.
 */

import { AlertTriangle, Sparkles, Table2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PersonalModeLimitCheck } from '../utils/personalModeLimits';
import { PERSONAL_MODE_MAX_ROWS, formatRowCountNumber } from '../utils/personalModeLimits';

type PersonalModeCloneTableLimitMessageProps = {
  limitCheck: PersonalModeLimitCheck;
  onUpgrade: () => void;
  onContinueSchemaOnly: () => void;
  onCancel: () => void;
};

/**
 * Message shown when table exceeds Personal mode row limits for cloning with data.
 * Offers upgrade prompt, schema-only option, and cancel option.
 */
export function PersonalModeCloneTableLimitMessage({
  limitCheck,
  onUpgrade,
  onContinueSchemaOnly,
  onCancel,
}: PersonalModeCloneTableLimitMessageProps) {
  const limitString = formatRowCountNumber(PERSONAL_MODE_MAX_ROWS);

  return (
    <div className="flex flex-col items-center justify-center p-6" data-testid="personal-mode-clone-table-limit">
      <div className="max-w-md text-center space-y-5">
        {/* Icon */}
        <div className="flex justify-center">
          <div className="p-3 rounded-full bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
            <AlertTriangle className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
          </div>
        </div>

        {/* Title */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">
            Large Table Detected
          </h3>
          <p className="text-sm text-muted-foreground">
            {limitCheck.description}
          </p>
        </div>

        {/* Limit explanation */}
        <div className="p-3 bg-muted/30 rounded-lg border border-border">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Table2 className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              Personal Mode Limit
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Personal mode limits cloning with data to tables with {limitString} rows or fewer.
            Schema-only cloning is always available.
          </p>
        </div>

        {/* Schema-only option */}
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Copy className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              Schema-Only Clone
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Clone the table structure without data. You can copy data separately.
          </p>
          <Button
            variant="outline"
            onClick={onContinueSchemaOnly}
            className="w-full"
            data-testid="schema-only-button"
          >
            Continue with Schema Only
          </Button>
        </div>

        {/* Upgrade prompt */}
        <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">
              Upgrade to Pro
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Clone tables of any size with data with Pro.
          </p>
          <div className="flex gap-2 justify-center">
            <Button
              variant="outline"
              onClick={onCancel}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button onClick={onUpgrade} className="gap-2" data-testid="upgrade-button">
              <Sparkles className="h-4 w-4" />
              Upgrade to Pro
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
